import React, { useState, useEffect } from "react";
import { Box, Typography, Card, CardContent, Button, Stepper, Step, StepLabel } from "@mui/material";
import { useParams } from "react-router-dom";
import { db, auth } from "../../firebase";
import { collection, doc, getDoc, getDocs, query, setDoc, Timestamp, where } from "firebase/firestore";
import Swal from "sweetalert2";

import StudentInfo from "./tabs/StudentInfo";
import FatherInfo from "./tabs/FatherInfo";
import MotherInfo from "./tabs/MotherInfo";
import GuardianInfo from "./tabs/GuardianInfo";
import MedicalRecord from "./tabs/MedicalRecord";
import Declaration from "./tabs/Declaration";
import { useNavigate } from "react-router-dom";

function Admissionstud() {

    const { studentId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({});
    const [activeStep, setActiveStep] = useState(0);
    const [schoolId, setSchoolId] = useState("");
    const [activeYear, setActiveYear] = useState(""); // 1. Added state for active year
    const steps = [
        "Student Information",
        "Father Information",
        "Mother Information",
        "Guardian / Emergency Contact",
        "Medical Record",
        "Declaration"
    ];

    useEffect(() => {
        const fetchContext = async () => {
            try {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser?.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);

                    // 2. Fetch the current active academic year for this school
                    const yearQuery = query(
                        collection(db, "academicyears"),
                        where("schoolId", "==", sId),
                        where("status", "==", "Active")
                    );
                    const yearSnap = await getDocs(yearQuery);
                    if (!yearSnap.empty) {
                        setActiveYear(yearSnap.docs[0].data().name);
                    }
                }
            } catch (err) {
                console.error("Context Error:", err);
            }
        };
        fetchContext();
    }, []);


    useEffect(() => {

        const loadStudent = async () => {

            if (!studentId) return;

            // ✅ DIRECT ADMISSION CASE
            if (studentId === "new") {
                setForm({
                    id: null,
                    firstname: "",
                    surname: "",
                    fathername: "",
                    mothername: "",
                    gender: "",
                    dob: "",
                    placeofbirth: "",
                    religion: "",
                    nationality: "Indian",
                    caste: "",
                    subcaste: "",
                    adhar: "",
                    portal: "",
                    language: "",
                    lastschool: "",
                    schoolId: schoolId || ""
                });
                return;
            }

            try {

                const studentRef = doc(db, "students", studentId);
                const studentSnap = await getDoc(studentRef);

                // ✅ EXISTING STUDENT
                if (studentSnap.exists()) {

                    const studentData = studentSnap.data();

                    const admissionSnap = await getDoc(doc(db, "admission", studentId));

                    const admissionData = admissionSnap.exists()
                        ? admissionSnap.data()
                        : {};

                    setForm({
                        id: studentId,
                        ...studentData,
                        ...admissionData
                    });

                }

                // ✅ FROM ENQUIRY
                else {

                    const enquiryRef = doc(db, "admissionEnquiries", studentId);
                    const enquirySnap = await getDoc(enquiryRef);

                    if (!enquirySnap.exists()) {
                        // Swal.fire("Error", "Student not found", "error");
                        return;
                    }

                    const data = enquirySnap.data();

                    let firstname = "";
                    let surname = "";
                    let fathername = "";

                    if (data.studentName) {
                        const parts = data.studentName.split(" ");
                        firstname = parts[0] || "";
                        fathername = parts[1] || "";
                        surname = parts[2] || "";
                    }

                    setForm({
                        id: studentId,
                        firstname,
                        surname,
                        fathername,
                        mothername: "",
                        gender: "",
                        dob: data.dob || "",
                        placeofbirth: data.birthPlace || "",
                        nationality: "Indian",
                        schoolId: data.schoolId || ""
                    });

                }

            } catch (err) {

                console.error(err);
                Swal.fire("Error", "Failed to load student data", "error");

            }

        };

        loadStudent();

    }, [studentId]);


    // ---------------- HANDLE CHANGE ----------------

    const handleChange = (field, value) => {

        setForm(prev => ({
            ...prev,
            [field]: value
        }));

    };


    // ---------------- SAVE DATA ----------------

    const saveStep = async () => {
        if (!studentId || !schoolId || !form.firstname || !form.surname || !form.fathername) {
            Swal.fire("Error", "Missing student or school ID or First name or Surname or Father Name", "error");
            return;
        }

        try {
            // STUDENT COLLECTION
            const studentData = {
                firstname: form.firstname || "",
                surname: form.surname || "",
                fathername: form.fathername || "",
                mothername: form.mothername || "",
                gender: form.gender || "",
                dob: form.dob || "",
                placeofbirth: form.placeofbirth || "",
                religion: form.religion || "",
                nationality: form.nationality || "",
                caste: form.caste || "",
                subcaste: form.subcaste || "",
                adhar: form.adhar || "",
                portal: form.portal || "",
                language: form.language || "",
                lastschool: form.lastschool || "",
                father_photo: form.father_photo || "",
                mother_photo: form.mother_photo || "",
                lastschool: form.lastschool || "",
                lastschoolmobile: form.lastschoolmobile || "",
                penno: form.penno || "",
                saralno: form.saralno || "",
                schooludiseno: form.schooludiseno || "",
                admno: form.admno || "",
                grno: form.grno || "",
                admissionType: form.admissionType || "",
                transport: form.transport || "",
                schoolId,
                updatedAt: Timestamp.now()
            };

            await setDoc(doc(db, "students", studentId), studentData, { merge: true });

            // ADMISSION COLLECTION
            const admissionData = {
                ...form,
                studentId,
                schoolId,
                academicYear: activeYear, // 3. Store the academic year here
                updatedAt: Timestamp.now(),
                createdAt: form.createdAt || Timestamp.now(),
                status: form.status || "Active",
                userid: auth.currentUser?.uid,
                admno: form.admno || "",
                grno: form.grno || "",
            };

            // Remove redundant student profile fields from admission doc
            const fieldsToRemove = [
                "firstname", "surname", "gender", "dob", "placeofbirth",
                "religion", "nationality", "caste", "subcaste", "adhar",
                "portal", "language", "lastschool"
            ];
            fieldsToRemove.forEach(field => delete admissionData[field]);

            await setDoc(doc(db, "admission", studentId), admissionData, { merge: true });

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    // ---------------- NAVIGATION ----------------

    const handleNext = async () => {

        await saveStep();

        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
        } else {
            handleFinish();
        }

    };

    const handleBack = () => {

        if (activeStep > 0) setActiveStep(activeStep - 1);

    };


    // ---------------- FINAL SUBMIT ----------------

    const handleFinish = async () => {

        try {

            await saveStep();

            Swal.fire("Success", "Admission Completed", "success");
            navigate('/admission-process');

        } catch (err) {

            Swal.fire("Error", err.message, "error");

        }

    };


    // ---------------- RENDER STEP ----------------

    const renderStep = () => {

        const tabProps = { form, handleChange, studentId, schoolId };

        switch (activeStep) {

            case 0:
                return <StudentInfo {...tabProps} />;

            case 1:
                return <FatherInfo {...tabProps} />;

            case 2:
                return <MotherInfo {...tabProps} />;

            case 3:
                return <GuardianInfo {...tabProps} />;

            case 4:
                return <MedicalRecord {...tabProps} />;

            case 5:
                return <Declaration {...tabProps} />;

            default:
                return null;

        }

    };


    // ---------------- UI ----------------

    return (

        <Box p={2} bgcolor="#f5f6fa" minHeight="100vh">

            <Typography variant="h6" fontWeight={700} mb={2}>
                Admission Process
            </Typography>


            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>

                <CardContent>

                    <Stepper activeStep={activeStep} alternativeLabel>

                        {steps.map((label, index) => (
                            <Step key={index}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}

                    </Stepper>

                </CardContent>

            </Card>


            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>

                {renderStep()}

                <Box mt={3} display="flex" justifyContent="space-between">

                    <Button
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{
                            borderColor: "#9e9e9e",
                            color: "#616161",
                            "&:hover": {
                                borderColor: "#424242",
                                backgroundColor: "#f5f5f5"
                            }
                        }}
                    >
                        Back
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{
                            backgroundColor: activeStep === steps.length - 1 ? "#2e7d32" : "#1976d2",
                            "&:hover": {
                                backgroundColor: activeStep === steps.length - 1 ? "#1b5e20" : "#1565c0"
                            }
                        }}
                    >
                        {activeStep === steps.length - 1 ? "Submit & Save Admission" : "Save & Next"}
                    </Button>

                </Box>

            </Card>

        </Box>

    );

}

export default Admissionstud;